import React from 'react'
import { withTranslation } from 'react-i18next'
import * as UIcons from '@iconscout/react-unicons'
import StatCard from 'components/DetailWidgets/StatCard'
import MultiLineChartCard from 'components/DetailWidgets/MultiLineChartCard'
import ConversionFunnel from 'components/DetailWidgets/ConversionFunnel'

const STAT_ICONS = {
  impressions: UIcons.UilEye,
  video_starts: UIcons.UilPlayCircle,
  view_completed: UIcons.UilCheckCircle,
  completion_rate: UIcons.UilPercentage,
  cta_clicks: UIcons.UilMouse,
  reward_claims: UIcons.UilGift,
  spend: UIcons.UilMoneyBill,
  cost_per_completed_view: UIcons.UilCreditCard
}

const CONVERSION_ICONS = {
  start_rate: UIcons.UilPlayCircle,
  completion_to_click: UIcons.UilCheckCircle,
  click_to_claim: UIcons.UilMouse,
  view_to_claim: UIcons.UilGift
}

const SPEND_ICONS = {
  cost_per_1000_impressions: UIcons.UilEye,
  cost_per_completed_view: UIcons.UilCheckCircle,
  cost_per_claim: UIcons.UilGift
}

const VIDEO_METRIC_ICONS = {
  video_length: UIcons.UilPlayCircle,
  avg_watch_time: UIcons.UilClockThree,
  drop_off_5s: UIcons.UilArrowDown,
  drop_off_10s: UIcons.UilArrowDown,
  asset_status: UIcons.UilSync
}

function ListCard({ title, children }) {
  return (
    <div className="w-full h-full rounded-2xl border border-border-lines p-4">
      <p className="text-base font-semibold text-dark-blue mb-4">{title}</p>
      <div className="flex flex-col gap-4">{children}</div>
    </div>
  )
}

function ConversionRateRow({ icon: Icon, label, formula, value }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="flex items-center gap-2.5 min-w-0">
        <div className="w-9 h-9 rounded-full bg-background flex items-center justify-center shrink-0">
          <Icon size={16} className="text-primary" />
        </div>
        <div className="min-w-0">
          <p className="text-sm text-dark-blue truncate">{label}</p>
          <p className="text-xs text-secondary truncate">{formula}</p>
        </div>
      </div>
      <p className="text-sm font-semibold text-dark-blue whitespace-nowrap">{value}%</p>
    </div>
  )
}

function SpendEfficiencyRow({ icon: Icon, label, value, trend }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="flex items-center gap-2.5 min-w-0">
        <div className="w-9 h-9 rounded-full bg-background flex items-center justify-center shrink-0">
          <Icon size={16} className="text-primary" />
        </div>
        <p className="text-sm text-dark-blue truncate">{label}</p>
      </div>
      <div className="text-right shrink-0">
        <p className="text-sm font-semibold text-dark-blue whitespace-nowrap">{value}</p>
        <span className="inline-flex items-center gap-0.5 text-xs font-medium text-success">
          <UIcons.UilArrowUp size={11} /> {trend}%
        </span>
      </div>
    </div>
  )
}

function VideoMetricRow({ icon: Icon, label, value, tone }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="flex items-center gap-2.5 min-w-0">
        <div className="w-9 h-9 rounded-full bg-background flex items-center justify-center shrink-0">
          <Icon size={16} className="text-primary" />
        </div>
        <p className="text-sm text-dark-blue truncate">{label}</p>
      </div>
      {
        tone
          ? <span className={`px-2 py-0.5 rounded text-xs font-semibold bg-${tone} bg-opacity-10 text-${tone}`}>{value}</span>
          : <p className="text-sm font-semibold text-dark-blue whitespace-nowrap">{value}</p>
      }
    </div>
  )
}

export default withTranslation()(function PerformanceTab({ t, campaign, dailyViews = [] }) {
  const { performance } = campaign
  const viewsSeries = dailyViews.length ? [{ key: 'views', label: 'Views', color: '#1976D2', data: dailyViews }] : []

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {
          performance.stats.map((stat) => (
            <StatCard
              key={stat.key}
              icon={STAT_ICONS[stat.key]}
              label={stat.label}
              value={stat.value}
              suffix={stat.suffix}
              trend={stat.trend}
              tone={stat.tone}
            />
          ))
        }
      </div>

      {
        viewsSeries.length > 0 && (
          <MultiLineChartCard title={t('rewardAds.detail.performance.views_over_time')} series={viewsSeries} />
        )
      }

      <MultiLineChartCard title={t('rewardAds.detail.performance.sourcing_vs_demand')} series={performance.sourcingVsDemand.series} />

      <div className="grid md:grid-cols-2 gap-4">
        <ConversionFunnel title={t('rewardAds.detail.performance.conversion_funnel')} steps={performance.funnel} />

        <ListCard title={t('rewardAds.detail.performance.conversion_rate')}>
          {
            performance.conversionRate.map((item) => (
              <ConversionRateRow
                key={item.key}
                icon={CONVERSION_ICONS[item.key]}
                label={item.label}
                formula={item.formula}
                value={item.value}
              />
            ))
          }
        </ListCard>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <ListCard title={t('rewardAds.detail.performance.spend_efficiency')}>
          {
            performance.spendEfficiency.map((item) => (
              <SpendEfficiencyRow
                key={item.key}
                icon={SPEND_ICONS[item.key]}
                label={item.label}
                value={item.value}
                trend={item.trend}
              />
            ))
          }
        </ListCard>

        <ListCard title={t('rewardAds.detail.performance.video_metrics')}>
          {
            performance.videoMetrics.map((item) => (
              <VideoMetricRow
                key={item.key}
                icon={VIDEO_METRIC_ICONS[item.key]}
                label={item.label}
                value={item.value}
                tone={item.tone}
              />
            ))
          }
        </ListCard>
      </div>
    </div>
  )
})
